import { Popover } from 'antd'
import {
  FileTextOutlined,
  CodeOutlined,
  BookOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { auxaliaColors } from '@shared/theme'
import { ROUTES } from '@shared/constants'
import { useVersionQuery } from '@queries/version'
import { APP_VERSION } from '@shared/config/version'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  try {
    return d.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return '—'
  }
}

export function AboutAXFlowAdminPopover({ open, onOpenChange, children }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data } = useVersionQuery()
  const version = data?.version ?? APP_VERSION
  const buildSha = data?.buildSha ? data.buildSha.slice(0, 7) : '—'
  const releasedAt = formatDate(data?.releasedAt)

  const disabledItems: { key: string; label: string; icon: ReactNode }[] = [
    { key: 'apiReference', label: t('sidebar.about.apiReference'), icon: <CodeOutlined /> },
    { key: 'documentation', label: t('sidebar.about.documentation'), icon: <BookOutlined /> },
    { key: 'licences', label: t('sidebar.about.licences'), icon: <InfoCircleOutlined /> },
  ]

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger="click"
      placement="topLeft"
      arrow={{ pointAtCenter: true }}
      styles={{ body: { padding: 6, borderRadius: 12, width: 300 } }}
      content={
        <div role="menu" aria-label={t('sidebar.about.ariaLabel')}>
          <div style={{ padding: '14px 12px 12px', background: auxaliaColors.navy, borderRadius: 8, margin: '2px 2px 6px', color: '#fff', display: 'flex', alignItems: 'center', gap: 12, position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${auxaliaColors.lime}, ${auxaliaColors.limeDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: auxaliaColors.navy, fontSize: 14, letterSpacing: '-0.02em', boxShadow: '0 4px 12px rgba(167,213,0,0.25)', flexShrink: 0 }}>
              AX
            </div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
                AX<span style={{ color: auxaliaColors.lime }}>Flow</span>
              </div>
              <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)', fontFamily: 'JetBrains Mono, monospace', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>v{version}</span>
                <span style={{ background: 'rgba(167,213,0,0.18)', color: auxaliaColors.lime, padding: '1px 6px', borderRadius: 3, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em' }}>
                  {t('sidebar.about.admin')}
                </span>
              </div>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.38)', fontFamily: 'JetBrains Mono, monospace', marginTop: 3 }}>
                {buildSha} · {releasedAt}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 11.5, color: '#4D5E6D' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a', boxShadow: '0 0 0 3px rgba(22,163,74,0.18)', flexShrink: 0 }} />
            <span>{t('sidebar.about.statusOk')}</span>
            <a href="#" aria-disabled="true" style={{ marginLeft: 'auto', fontSize: 11, color: auxaliaColors.limeDark, textDecoration: 'none' }}>
              {t('sidebar.about.statusLink')} ↗
            </a>
          </div>

          <div style={{ height: 1, background: auxaliaColors.grayLight, margin: '4px 8px' }} />

          <button
            role="menuitem"
            type="button"
            onClick={() => { onOpenChange(false); navigate(ROUTES.WHATS_NEW) }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 7,
              fontSize: 13, color: '#0F1B24', cursor: 'pointer',
              background: 'transparent', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit',
            }}
          >
            <span style={{ color: '#8393A2', display: 'inline-flex' }}><FileTextOutlined /></span>
            <span style={{ flex: 1 }}>{t('sidebar.about.releaseNotes')}</span>
          </button>

          {disabledItems.map((it) => (
            <div
              key={it.key}
              role="menuitem"
              aria-disabled="true"
              tabIndex={-1}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 7, fontSize: 13, color: '#0F1B24', opacity: 0.5, cursor: 'not-allowed' }}
            >
              <span style={{ color: '#8393A2', display: 'inline-flex' }}>{it.icon}</span>
              <span style={{ flex: 1 }}>{it.label}</span>
            </div>
          ))}
        </div>
      }
    >
      {children}
    </Popover>
  )
}
